import React, { useState, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * AttributeTable — spreadsheet-style feature properties viewer
 *
 * Props:
 *   features - GeoJSON features array
 *   layerName - name of the layer being viewed
 *   onFeatureClick(feature) - highlight feature on map
 *   onClose() - close table
 *   selectedFeatureId - currently selected feature ID (for highlighting row)
 */
export default function AttributeTable({
  features = [], layerName = '', onFeatureClick, onClose, selectedFeatureId,
  // XYZ pipeline extras (all optional)
  zFields = [],           // Z column names to highlight
  activeZ = null,         // Z field used for row coloring
  zStats = null,          // { min, max } of activeZ for the color ramp
  onToggleExclude = null, // (rowIdx) => toggle _excluded (right-click)
}) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const thStyle = {
    textAlign: 'left',
    padding: '6px 10px',
    fontSize: 10,
    color: C.text3,
    textTransform: 'uppercase',
    fontWeight: 500,
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: 'nowrap',
  };
  const tdStyle = {
    padding: '5px 10px',
    color: C.text1,
    whiteSpace: 'nowrap',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Collect all property keys across features (hide internal fields)
  const columns = useMemo(() => {
    const keys = new Set();
    features.forEach(f => {
      if (f.properties) Object.keys(f.properties).forEach(k => keys.add(k));
    });
    keys.delete('_row');
    keys.delete('_excluded');
    return Array.from(keys);
  }, [features]);

  const excludedCount = useMemo(
    () => features.filter(f => f.properties?._excluded).length,
    [features]
  );

  // Row tint by activeZ value — same cool→warm ramp as the map
  const zRowColor = (f) => {
    if (!activeZ || !zStats || zStats.max <= zStats.min) return null;
    const v = f.properties?.[activeZ];
    if (typeof v !== 'number' || !isFinite(v)) return null;
    const t = Math.max(0, Math.min(1, (v - zStats.min) / (zStats.max - zStats.min)));
    const stops = [[37, 99, 235], [6, 182, 212], [52, 211, 153], [251, 191, 36], [248, 113, 113]];
    const pos = t * (stops.length - 1);
    const lo = Math.floor(pos), hi = Math.min(stops.length - 1, lo + 1), fr = pos - lo;
    const rgb = stops[lo].map((c, i) => Math.round(c + (stops[hi][i] - c) * fr));
    return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.14)`;
  };

  // Filter
  const filtered = useMemo(() => {
    if (!filterText) return features;
    const lower = filterText.toLowerCase();
    return features.filter(f =>
      columns.some(k => String(f.properties?.[k] ?? '').toLowerCase().includes(lower))
    );
  }, [features, filterText, columns]);

  // Sort
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const va = a.properties?.[sortKey] ?? '';
      const vb = b.properties?.[sortKey] ?? '';
      const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  // Paginate
  const pageCount = Math.ceil(sorted.length / pageSize);
  const visible = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div style={{ background: C.bg2, borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', maxHeight: 300 }}>
      {/* Header */}
      <div style={{ padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>📋 {layerName}</span>
          <span style={{ fontSize: 11, color: C.text3 }}>{filtered.length} features</span>
          {excludedCount > 0 && (
            <span style={{ fontSize: 10, color: C.text2 }}>
              {excludedCount} excluded from gridding
            </span>
          )}
          {onToggleExclude && (
            <span style={{ fontSize: 9, color: C.text3 }}>right-click a row to exclude/include</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input value={filterText} onChange={e => { setFilterText(e.target.value); setPage(0); }}
            placeholder="Filter..."
            style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', width: 160 }} />
          {pageCount > 1 && (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>‹</button>
              <span style={{ fontSize: 10, color: C.text2 }}>{page + 1}/{pageCount}</span>
              <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>›</button>
            </div>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflow: 'auto', flex: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, background: C.bg1, zIndex: 2 }}>
              <th style={{ ...thStyle, width: 40 }}>#</th>
              {columns.map(col => {
                const isZ = zFields.includes(col);
                const isActiveZ = col === activeZ;
                return (
                  <th key={col} onClick={() => handleSort(col)}
                    style={{
                      ...thStyle, cursor: 'pointer', userSelect: 'none',
                      color: isActiveZ ? '#fbbf24' : isZ ? '#eab308' : thStyle.color,
                      borderBottom: isActiveZ ? '2px solid #fbbf24' : thStyle.borderBottom,
                    }}>
                    {isZ && <span style={{ marginRight: 3 }}>Z</span>}
                    {col}
                    {sortKey === col && <span style={{ marginLeft: 4, color: C.cyan }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((f, i) => {
              const isSelected = selectedFeatureId && f.properties?.id === selectedFeatureId;
              const isExcluded = !!f.properties?._excluded;
              const zTint = !isSelected && !isExcluded ? zRowColor(f) : null;
              return (
                <tr key={i} onClick={() => onFeatureClick?.(f)}
                  onContextMenu={onToggleExclude ? (e) => {
                    e.preventDefault();
                    if (f.properties?._row !== undefined) onToggleExclude(f.properties._row);
                  } : undefined}
                  title={onToggleExclude ? (isExcluded ? 'Right-click to include in gridding' : 'Right-click to exclude from gridding') : undefined}
                  style={{
                    borderBottom: `1px solid ${C.border}`, cursor: 'pointer',
                    background: isSelected ? 'rgba(0,212,255,0.1)' : (zTint || 'transparent'),
                    opacity: isExcluded ? 0.45 : 1,
                    textDecoration: isExcluded ? 'line-through' : 'none',
                  }}
                  onMouseEnter={e => { if (!isSelected && !zTint) e.currentTarget.style.background = C.bg3; }}
                  onMouseLeave={e => { if (!isSelected && !zTint) e.currentTarget.style.background = 'transparent'; }}>
                  <td style={{ ...tdStyle, color: isExcluded ? C.text3 : tdStyle.color }}>
                    {isExcluded ? '✗' : page * pageSize + i + 1}
                  </td>
                  {columns.map(col => (
                    <td key={col} style={tdStyle}>
                      {formatValue(f.properties?.[col])}
                    </td>
                  ))}
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={columns.length + 1} style={{ ...tdStyle, textAlign: 'center', color: C.text3 }}>No features</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatValue(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? '✓' : '✗';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}
